import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { API_BASE_URL, API_ENDPOINTS } from '../../config/api';

/** Query params for GET /api/Erp/order-tracking */
export interface OrderTrackingQueryParams {
  startDate?: string;
  endDate?: string;
}

/** API response item from GET /api/Erp/order-tracking */
export interface OrderTrackingItem {
  siparisNo: string;
  cariIsim: string | null;
  stokAdi: string;
  siparisMiktari: number;
  birimFiyat: number;
  paraBirim: string;
  teslimTarihi: string | null;
  stharTarih: string | null;
  faturaNo: string | null;
  enSonKiFaturaTarihi: string | null;
  gelenMiktar: number | null;
  gelenBirimFiyat: number | null;
  gelenParaBirim: string;
}

interface OrderTrackingResponse {
  success?: boolean;
  message?: string;
  data?: OrderTrackingItem[];
}

interface ErpState {
  orderTracking: {
    loading: boolean;
    data: OrderTrackingItem[] | null;
    error: string | null;
  };
}

const initialState: ErpState = {
  orderTracking: {
    loading: false,
    data: null,
    error: null,
  },
};

export const getOrderTracking = createAsyncThunk(
  'erp/getOrderTracking',
  async (params: OrderTrackingQueryParams = {}, thunkAPI) => {
    try {
      const queryParams = new URLSearchParams();
      if (params.startDate) queryParams.append('startDate', params.startDate);
      if (params.endDate) queryParams.append('endDate', params.endDate);
      const queryString = queryParams.toString();
      const url = `${API_BASE_URL}${API_ENDPOINTS.ERP.ORDER_TRACKING}${queryString ? `?${queryString}` : ''}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache',
          Expires: '0',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const json: OrderTrackingResponse = await response.json();
      return json.data ?? [];
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Sipariş takibi yüklenirken hata oluştu';
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

const erpSlice = createSlice({
  name: 'erp',
  initialState,
  reducers: {
    clearOrderTracking: (state) => {
      state.orderTracking = {
        loading: false,
        data: null,
        error: null,
      };
    },
    clearOrderTrackingError: (state) => {
      state.orderTracking.error = null;
    },
  },
  extraReducers(builder) {
    builder.addCase(getOrderTracking.pending, (state) => {
      state.orderTracking.loading = true;
      state.orderTracking.error = null;
    });
    builder.addCase(getOrderTracking.fulfilled, (state, action) => {
      state.orderTracking.loading = false;
      state.orderTracking.data = Array.isArray(action.payload) ? action.payload : null;
      state.orderTracking.error = null;
    });
    builder.addCase(getOrderTracking.rejected, (state, action) => {
      state.orderTracking.loading = false;
      state.orderTracking.error =
        typeof action.payload === 'string' ? action.payload : 'Sipariş takibi yüklenirken hata oluştu';
    });
  },
});

export const { clearOrderTracking, clearOrderTrackingError } = erpSlice.actions;
export default erpSlice.reducer;
